import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Alert,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, Select,
  FormControl, InputLabel, Card, CardContent, Tooltip,
  InputAdornment, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { FileDownload, Search, BeachAccess, Warning } from '@mui/icons-material';
import { apiFetch } from '../../services/apiFetch';

interface LeaveType { id: string; name: string; }
interface BalanceEntry {
  leaveTypeName: string;
  opening: number; accrued: number; used: number;
  adjusted: number; carryOver: number; remaining: number;
}
interface EmployeeRow {
  employeeId: string;
  name: string;
  employeeNumber?: string;
  department?: string;
  departmentId?: string;
  position?: string;
  balances: Record<string, BalanceEntry>;
}
interface OrgSummary { year: number; leaveTypes: LeaveType[]; employees: EmployeeRow[]; }
interface Department { id: string; name: string; }

function colorForRemaining(remaining: number, accrued: number): string {
  if (accrued <= 0) return 'inherit';
  const pct = accrued > 0 ? remaining / accrued : 1;
  if (remaining < 0)  return '#d32f2f';   // overdrawn
  if (pct <= 0.1)     return '#f57c00';   // almost gone
  if (pct >= 0.9)     return '#1976d2';   // lots left (might not be taking leave)
  return '#388e3c';                        // healthy
}

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

const LeaveBalanceReport: React.FC = () => {
  const [data, setData]         = useState<OrgSummary | null>(null);
  const [departments, setDepts] = useState<Department[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [year, setYear]         = useState(new Date().getFullYear());
  const [deptFilter, setDeptFilter] = useState('');
  const [search, setSearch]     = useState('');
  const [view, setView]         = useState<'remaining' | 'used' | 'full'>('remaining');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year) });
      if (deptFilter) params.append('departmentId', deptFilter);

      const [sRes, dRes] = await Promise.all([
        apiFetch(`/api/leave/balances/org-summary?${params}`),
        apiFetch('/api/departments'),
      ]);
      if (!sRes.ok) throw new Error('Failed to load leave balances');
      const [sData, dData] = await Promise.all([sRes.json(), dRes.json()]);
      setData(sData);
      setDepts(dData);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [year, deptFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search) return data.employees;
    const q = search.toLowerCase();
    return data.employees.filter((e) =>
      e.name.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q) ||
      e.position?.toLowerCase().includes(q),
    );
  }, [data, search]);

  const exportCsv = () => {
    if (!data) return;
    const params = new URLSearchParams({ year: String(year) });
    if (deptFilter) params.append('departmentId', deptFilter);
    apiFetch(`/api/leave/balances/org-summary/export.csv?${params}`)
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `leave-balance-${year}.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  // Summary stats
  const stats = useMemo(() => {
    if (!data) return null;
    const employees = filtered;
    const totalBalances = data.leaveTypes.map((lt) => ({
      id: lt.id,
      name: lt.name,
      totalUsed: employees.reduce((s, e) => s + (e.balances[lt.id]?.used ?? 0), 0),
      totalRemaining: employees.reduce((s, e) => s + (e.balances[lt.id]?.remaining ?? 0), 0),
      employeesWithBalance: employees.filter((e) => (e.balances[lt.id]?.remaining ?? 0) > 0).length,
    }));
    const noBalanceEmployees = employees.filter((e) => Object.keys(e.balances).length === 0).length;
    return { totalBalances, noBalanceEmployees };
  }, [data, filtered]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;

  const leaveTypes = data?.leaveTypes ?? [];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Leave Balance Report</Typography>
          <Typography variant="body2" color="text.secondary">
            Organisation-wide leave balances by employee and leave type
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<FileDownload />} onClick={exportCsv} disabled={!data}>
          Export CSV
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards by leave type */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {stats.totalBalances.map((lt) => (
            <Grid key={lt.id} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">{lt.name}</Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">{lt.totalRemaining.toFixed(1)}</Typography>
                  <Typography variant="caption" color="text.secondary">days remaining</Typography>
                  <Typography variant="caption" color="error" display="block">{lt.totalUsed.toFixed(1)} used</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {stats.noBalanceEmployees > 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" icon={<Warning />}>
                {stats.noBalanceEmployees} employee{stats.noBalanceEmployees !== 1 ? 's' : ''} have no leave balances initialised for {year}.
                Go to Leave Management → run Apply Policy or initialise balances individually.
              </Alert>
            </Grid>
          )}
        </Grid>
      )}

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))} label="Year">
            {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Department</InputLabel>
          <Select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} label="Department">
            <MenuItem value="">All Departments</MenuItem>
            {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small" placeholder="Search employee / dept…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 200 }}
        />
        <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
          <ToggleButton value="remaining">Remaining</ToggleButton>
          <ToggleButton value="used">Used</ToggleButton>
          <ToggleButton value="full">Full Detail</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filtered.length} employees
        </Typography>
      </Box>

      {/* Matrix table */}
      {leaveTypes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BeachAccess sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No leave balances found for {year}.</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Apply leave policy in Leave Management to initialise balances.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflowX: 'auto' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 160, position: 'sticky', left: 0, bgcolor: 'grey.50', zIndex: 3 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 130, bgcolor: 'grey.50' }}>Department</TableCell>
                  {leaveTypes.map((lt) => (
                    view === 'full' ? (
                      [
                        <TableCell key={`${lt.id}-used`} sx={{ fontWeight: 700, minWidth: 80, bgcolor: 'grey.50', textAlign: 'center' }}>
                          {lt.name}<br /><Typography variant="caption" color="text.secondary">Used</Typography>
                        </TableCell>,
                        <TableCell key={`${lt.id}-rem`} sx={{ fontWeight: 700, minWidth: 80, bgcolor: 'grey.50', textAlign: 'center' }}>
                          {lt.name}<br /><Typography variant="caption" color="text.secondary">Remaining</Typography>
                        </TableCell>,
                      ]
                    ) : (
                      <TableCell key={lt.id} sx={{ fontWeight: 700, minWidth: 90, bgcolor: 'grey.50', textAlign: 'center' }}>
                        {lt.name}
                      </TableCell>
                    )
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((emp) => (
                  <TableRow key={emp.employeeId} hover>
                    <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{emp.name}</Typography>
                      {emp.employeeNumber && <Typography variant="caption" color="text.secondary">#{emp.employeeNumber}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{emp.department || '—'}</Typography>
                    </TableCell>
                    {leaveTypes.map((lt) => {
                      const b = emp.balances[lt.id];
                      if (!b) {
                        return view === 'full'
                          ? [
                            <TableCell key={`${emp.employeeId}-${lt.id}-u`} sx={{ textAlign: 'center', color: 'text.disabled' }}>—</TableCell>,
                            <TableCell key={`${emp.employeeId}-${lt.id}-r`} sx={{ textAlign: 'center', color: 'text.disabled' }}>—</TableCell>,
                          ]
                          : <TableCell key={`${emp.employeeId}-${lt.id}`} sx={{ textAlign: 'center', color: 'text.disabled' }}>—</TableCell>;
                      }
                      const remColor = colorForRemaining(b.remaining, b.accrued + b.opening);
                      if (view === 'full') {
                        return [
                          <TableCell key={`${emp.employeeId}-${lt.id}-u`} sx={{ textAlign: 'center' }}>
                            <Typography variant="body2">{b.used.toFixed(1)}</Typography>
                          </TableCell>,
                          <TableCell key={`${emp.employeeId}-${lt.id}-r`} sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: remColor }}>{b.remaining.toFixed(1)}</Typography>
                          </TableCell>,
                        ];
                      }
                      const val = view === 'used' ? b.used : b.remaining;
                      const color = view === 'used' ? 'inherit' : remColor;
                      return (
                        <Tooltip key={`${emp.employeeId}-${lt.id}`}
                          title={`Used: ${b.used} | Remaining: ${b.remaining} | Accrued: ${b.accrued} | Carry-over: ${b.carryOver}`}>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" fontWeight={500} sx={{ color }}>
                              {val.toFixed(1)}
                            </Typography>
                          </TableCell>
                        </Tooltip>
                      );
                    })}
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={leaveTypes.length * (view === 'full' ? 2 : 1) + 2} align="center">
                      <Typography color="text.secondary" py={3}>No employees match current filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default LeaveBalanceReport;
